import React, { useState } from 'react';
import {
  X,
  FileText,
  Plus,
  Trash2,
  Copy,
  ExternalLink,
  ShieldCheck,
  Trophy,
  Package,
  Settings,
  Globe,
  Check,
  Pencil,
} from 'lucide-react';
import type { PageRecord } from '../types';

export interface PageManagerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  pages: PageRecord[];
  activePageId: string;
  onSelectPage: (pageId: string) => void;
  onNewPage: () => void;
  onRenamePage?: (pageId: string, title: string, routePath: string) => Promise<void> | void;
  onDeletePage?: (pageId: string) => void;
  onDuplicatePage?: (page: PageRecord) => void;
  onUpdatePageSeo?: (pageId: string, seo: { title: string; description: string }) => void;
  onQuickCreatePage?: (title: string, route: string, presetId: string) => void;
}

const TURNKEY_PAGE_PRESETS = [
  { title: 'Toppers Wall of Fame', route: '/toppers', presetId: 'toppers', icon: Trophy },
  { title: 'Digital Study Material Store', route: '/study-material', presetId: 'digital_products', icon: Package },
  { title: 'Terms & Conditions', route: '/terms-and-conditions', presetId: 'terms', icon: ShieldCheck },
  { title: 'Privacy Policy', route: '/privacy-policy', presetId: 'privacy', icon: ShieldCheck },
  { title: 'Cookie Policy', route: '/cookie-policy', presetId: 'cookie', icon: ShieldCheck },
];

export function PageManagerDrawer({
  isOpen,
  onClose,
  pages,
  activePageId,
  onSelectPage,
  onNewPage,
  onRenamePage,
  onDeletePage,
  onDuplicatePage,
  onUpdatePageSeo,
  onQuickCreatePage,
}: PageManagerDrawerProps): React.JSX.Element | null {
  const [editingSeoPageId, setEditingSeoPageId] = useState<string | null>(null);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDesc, setSeoDesc] = useState('');

  const [editingRenamePageId, setEditingRenamePageId] = useState<string | null>(null);
  const [renameTitle, setRenameTitle] = useState('');
  const [renameRoutePath, setRenameRoutePath] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  if (!isOpen) return null;

  const startEditSeo = (p: PageRecord) => {
    setEditingRenamePageId(null);
    setEditingSeoPageId(p.id);
    setSeoTitle(p.title || '');
    setSeoDesc('');
  };

  const saveSeo = (pageId: string) => {
    onUpdatePageSeo?.(pageId, { title: seoTitle, description: seoDesc });
    setEditingSeoPageId(null);
  };

  const startRename = (p: PageRecord) => {
    setEditingSeoPageId(null);
    setEditingRenamePageId(p.id);
    setRenameTitle(p.title || '');
    setRenameRoutePath(p.route_path || '');
  };

  const saveRename = async (pageId: string, isHome: boolean) => {
    if (!renameTitle.trim()) return;
    const path = isHome
      ? '/'
      : renameRoutePath.trim().startsWith('/')
      ? renameRoutePath.trim()
      : `/${renameRoutePath.trim()}`;
    setIsRenaming(true);
    try {
      await onRenamePage?.(pageId, renameTitle.trim(), path);
      setEditingRenamePageId(null);
    } finally {
      setIsRenaming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex justify-end">
      <div
        className="bg-card border-l border-border w-full max-w-md h-full shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20 shrink-0">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-sm font-bold text-foreground">Pages &amp; Navigation</h2>
              <p className="text-xs text-muted-foreground">{pages.length} pages in this website</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onNewPage}
              className="px-2.5 py-1 rounded-lg bg-primary text-primary-foreground text-xs font-semibold flex items-center gap-1 hover:opacity-90 transition-opacity cursor-pointer shadow-xs"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>New Page</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Existing Pages List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
            Site Pages
          </div>

          <div className="space-y-2">
            {pages.map((p) => {
              const isActive = p.id === activePageId;
              const isHome = p.route_path === '/';

              return (
                <div
                  key={p.id}
                  className={`rounded-xl border p-3 transition-all ${
                    isActive
                      ? 'border-primary bg-primary/5 shadow-xs'
                      : 'border-border bg-card hover:border-border/80'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div
                      className="cursor-pointer flex-1"
                      onClick={() => {
                        onSelectPage(p.id);
                        onClose();
                      }}
                    >
                      <div className="flex items-center gap-1.5 font-bold text-xs text-foreground">
                        <span>{p.title}</span>
                        {isHome && (
                          <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.2 rounded font-semibold">
                            Home
                          </span>
                        )}
                        {isActive && (
                          <span className="h-2 w-2 rounded-full bg-emerald-500 inline-block ml-1" />
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-muted-foreground mt-0.5">
                        {p.route_path}
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      {onRenamePage && (
                        <button
                          type="button"
                          onClick={() => startRename(p)}
                          className="p-1.5 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                          title="Rename Page & Slug"
                          data-testid={`rename-page-btn-${p.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => startEditSeo(p)}
                        className="p-1.5 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                        title="Edit Page SEO Meta"
                      >
                        <Settings className="h-3.5 w-3.5" />
                      </button>

                      {onDuplicatePage && (
                        <button
                          type="button"
                          onClick={() => onDuplicatePage(p)}
                          className="p-1.5 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                          title="Duplicate Page"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      )}

                      {onDeletePage && !isHome && (
                        <button
                          type="button"
                          onClick={() => onDeletePage(p.id)}
                          className="p-1.5 hover:bg-rose-500/10 rounded text-rose-500 hover:text-rose-600 transition-colors cursor-pointer"
                          title="Delete Page"
                          data-testid={`delete-page-btn-${p.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Inline Rename Editor when active */}
                  {editingRenamePageId === p.id && (
                    <div className="mt-3 pt-3 border-t border-border/60 space-y-2 text-xs bg-muted/10 p-2.5 rounded-lg border border-border/40">
                      <div className="font-semibold text-foreground text-[11px] flex items-center gap-1">
                        <Pencil className="h-3 w-3 text-primary" />
                        <span>Rename Page</span>
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-muted-foreground block mb-0.5">
                          Page Title
                        </label>
                        <input
                          type="text"
                          value={renameTitle}
                          onChange={(e) => setRenameTitle(e.target.value)}
                          className="w-full p-1.5 text-xs rounded border border-input bg-background text-foreground focus:ring-1 focus:ring-primary focus:outline-none"
                          placeholder="e.g. Courses & Batches"
                          data-testid="page-rename-title-input"
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-muted-foreground block mb-0.5">
                          Route Path / Slug
                        </label>
                        <input
                          type="text"
                          value={isHome ? '/' : renameRoutePath}
                          disabled={isHome}
                          onChange={(e) => setRenameRoutePath(e.target.value)}
                          className={`w-full p-1.5 text-xs rounded border border-input font-mono focus:ring-1 focus:ring-primary focus:outline-none ${
                            isHome
                              ? 'bg-muted/40 text-muted-foreground cursor-not-allowed'
                              : 'bg-background text-foreground'
                          }`}
                          placeholder="e.g. /courses"
                          data-testid="page-rename-route-input"
                        />
                        {isHome && (
                          <span className="text-[10px] text-muted-foreground mt-0.5 block">
                            (Root route '/' is protected and fixed)
                          </span>
                        )}
                      </div>
                      <div className="flex justify-end gap-1.5 pt-1">
                        <button
                          type="button"
                          onClick={() => setEditingRenamePageId(null)}
                          className="px-2.5 py-1 rounded border border-border text-[11px] text-muted-foreground hover:bg-muted cursor-pointer"
                          data-testid="page-rename-cancel-btn"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={isRenaming || !renameTitle.trim()}
                          onClick={() => saveRename(p.id, isHome)}
                          className="px-3 py-1 rounded bg-primary text-primary-foreground font-semibold text-[11px] flex items-center gap-1 hover:opacity-90 disabled:opacity-50 cursor-pointer shadow-xs"
                          data-testid="page-rename-save-btn"
                        >
                          <Check className="h-3 w-3" />
                          <span>{isRenaming ? 'Saving...' : 'Save'}</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Inline SEO Editor when active */}
                  {editingSeoPageId === p.id && (
                    <div className="mt-3 pt-3 border-t border-border/60 space-y-2 text-xs">
                      <div>
                        <label className="text-[10px] font-semibold text-muted-foreground block mb-0.5">
                          Browser Title &amp; SEO Meta Title
                        </label>
                        <input
                          type="text"
                          value={seoTitle}
                          onChange={(e) => setSeoTitle(e.target.value)}
                          className="w-full p-1.5 text-xs rounded border border-input bg-background text-foreground"
                          placeholder="e.g. JEE Preparation Courses - Helios"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-muted-foreground block mb-0.5">
                          Meta Description
                        </label>
                        <textarea
                          rows={2}
                          value={seoDesc}
                          onChange={(e) => setSeoDesc(e.target.value)}
                          className="w-full p-1.5 text-xs rounded border border-input bg-background text-foreground resize-none"
                          placeholder="Comprehensive entrance coaching for JEE & NEET with top rankers..."
                        />
                      </div>
                      <div className="flex justify-end gap-1.5 pt-1">
                        <button
                          type="button"
                          onClick={() => setEditingSeoPageId(null)}
                          className="px-2 py-0.5 rounded border border-border text-[11px] text-muted-foreground hover:bg-muted"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => saveSeo(p.id)}
                          className="px-2.5 py-0.5 rounded bg-primary text-primary-foreground font-semibold text-[11px] flex items-center gap-1 hover:opacity-90"
                        >
                          <Check className="h-3 w-3" /> Save SEO
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Quick-Add Turnkey Coaching Pages */}
          {onQuickCreatePage && (
            <div className="pt-4 mt-2 border-t border-border space-y-2.5">
              <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                1-Click Turnkey Academy Pages
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Add standard coaching institution pages complete with pre-configured legal compliance or storefront blocks:
              </p>
              <div className="space-y-1.5">
                {TURNKEY_PAGE_PRESETS.map((item) => {
                  const Icon = item.icon;
                  const alreadyExists = pages.some((p) => p.route_path === item.route);

                  return (
                    <button
                      key={item.route}
                      type="button"
                      disabled={alreadyExists}
                      onClick={() => onQuickCreatePage(item.title, item.route, item.presetId)}
                      className="w-full p-2 rounded-lg border border-border bg-card hover:bg-muted/60 disabled:opacity-40 disabled:pointer-events-none transition-colors flex items-center justify-between text-left text-xs cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
                        <div>
                          <div className="font-semibold text-foreground">{item.title}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{item.route}</div>
                        </div>
                      </div>
                      <span className="text-[10px] font-semibold text-primary">
                        {alreadyExists ? 'Added' : '+ Add'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="p-3 border-t border-border bg-muted/20 px-4 flex items-center justify-between text-xs text-muted-foreground shrink-0">
          <span>Target Subdomain: *.sites.thesci.co</span>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 rounded-lg border border-border hover:bg-muted text-foreground cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
